// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeTransit =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const shouldSerializeJson =
    headers && headers['Content-Type'] === 'application/json' && body;

  const serializedBody = shouldSerializeTransit
    ? serialize(body)
    : shouldSerializeJson
    ? JSON.stringify(body)
    : body;

  const bodyMaybe = serializedBody ? { body: serializedBody } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        const errorMsg = data.message || data.error || 'API request failed';
        let e = new Error(errorMsg);
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const createStripeCheckoutSession = () => {
  return post('/api/create-stripe-checkout-session', {}, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const verifyCheckoutSession = body => {
  return post('/api/verify-checkout-session', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const createStripePortalSession = () => {
  return post('/api/create-stripe-portal-session', {}, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const createStripeSubscription = body => {
  return post('/api/create-stripe-subscription', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const fetchShippingRates = body => {
  return post('/api/shipping-rates', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const createShippingLabel = body => {
  return post('/api/create-shipping-label', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const confirmPickup = body => {
  return post('/api/confirm-pickup', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const fetchTrackingStatus = body => {
  return post('/api/tracking-status', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// ================ Admin Order Approval ================ //

export const fetchAdminPendingTransactions = () => {
  return request('/api/admin-pending-transactions', {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminApproveTransaction = transactionId => {
  return post('/api/admin-approve-transaction', { transactionId }, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminRejectTransaction = transactionId => {
  return post('/api/admin-reject-transaction', { transactionId }, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// ================ Admin Dashboard (vendors & products) ================ //

export const fetchAdminVendors = (params = {}) => {
  const search = new URLSearchParams(params).toString();
  return request(`/api/admin-vendors${search ? `?${search}` : ''}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminCreateVendor = body => {
  return post('/api/admin-create-vendor', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminImportVendors = body => {
  return post('/api/admin-import-vendors', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const fetchAdminProducts = (params = {}) => {
  const search = new URLSearchParams(params).toString();
  return request(`/api/admin-products${search ? `?${search}` : ''}`, {
    method: methods.GET,
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminCreateProduct = body => {
  return post('/api/admin-create-product', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminUpdateProduct = body => {
  return post('/api/admin-update-product', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminUploadProductImage = body => {
  return post('/api/admin-upload-image', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminImportProducts = body => {
  return post('/api/admin-import-products', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const adminBulkUpdateProducts = body => {
  return post('/api/admin-bulk-update-products', body, {
    headers: { 'Content-Type': 'application/json' },
  });
};
